import { CalendarClock, ListChecks } from 'lucide-react'

/**
 * "What's included" card for the Usage Report pages — a metric-tiles grid
 * plus a cadence / delivery footer callout. Shared by the Company Admin
 * (`pages/app/UsageReportPage`) and Master Admin
 * (`pages/platform/UsageReportPage`) so the two pages can't drift visually.
 *
 * Props:
 *   - metrics: [{ icon, label, hint, tone }] — tone is a key of TONE below.
 *   - footerNote: string shown after the cadence line (delivery / fallback).
 *   - cadenceNote: the cadence sentence in the footer. Defaults to the fixed
 *     weekly/monthly wording (the Company Admin page, whose cadence can't change);
 *     the Master Admin page passes a value reflecting its selectable cadence.
 */

// Soft-tinted icon tiles per metric — same palette convention as the Report
// Schedule cards, with dark-mode variants so tiles stay legible on both themes.
export const USAGE_TONE = {
  sky: 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
  violet: 'bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
  emerald: 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
  amber: 'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  rose: 'bg-[#FFE4E6] text-[#E11D48] border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
}

export function UsageReportIncludedCard({
  metrics,
  footerNote,
  cadenceNote = 'Sent weekly (Monday) or monthly (1st of the month)',
}) {
  return (
    <section className="mb-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
      <div className="px-5 pt-5 pb-4">
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 flex items-center gap-2">
          <ListChecks size={18} className="text-[color:var(--color-accent-strong)]" />
          What&rsquo;s included
        </h2>
        <p className="mt-1.5 text-sm text-[color:var(--color-ink-muted)]">
          Each report covers the period&rsquo;s figures with a change against
          the previous period. It never includes the content of anyone&rsquo;s
          submitted reports.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="flex items-start gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3.5 py-3 transition-all hover:border-[color:var(--color-rule-strong)] hover:shadow-sm"
            >
              <span
                className={
                  'h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ' +
                  USAGE_TONE[m.tone]
                }
                aria-hidden="true"
              >
                <m.icon size={18} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] m-0">
                  {m.label}
                </p>
                <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
                  {m.hint}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cadence + delivery callout */}
      <div className="px-5 py-3 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--color-ink)]">
          <CalendarClock size={14} className="text-[color:var(--color-accent-strong)]" />
          {cadenceNote}
        </span>
        <span className="hidden sm:block h-3.5 w-px bg-[color:var(--color-rule-strong)]" aria-hidden="true" />
        <span className="text-xs text-[color:var(--color-ink-muted)]">{footerNote}</span>
      </div>
    </section>
  )
}
